/**
 * 阿里云 ARMS 监控自定义事件上报工具
 * 参考文档: https://help.aliyun.com/zh/arms/user-experience-monitoring/web-h5-sdk-configuration-reference
 */

// 扩展 Window 类型，添加 ARMS SDK 的全局对象
declare global {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    interface Window {
        RumSDK?: {
            default: {
                sendCustom: (event: {
                    type: string;
                    name: string;
                    group?: string;
                    value?: number;
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    properties?: Record<string, any>;
                }) => void;
                sendException: (exception: {
                    name: string;
                    message: string;
                    file?: string;
                    stack?: string;
                    line?: number;
                    column?: number;
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    properties?: Record<string, any>;
                }) => void;
                sendResource: (resource: {
                    name: string;
                    type: string;
                    duration: number;
                    success?: number;
                    method?: string;
                    status_code?: number | string;
                    message?: string;
                    url?: string;
                    trace_id?: string;
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    properties?: Record<string, any>;
                }) => void;
                setConfig: (config: Record<string, unknown>) => void;
            };
        };
    }
}

/**
 * 获取 ARMS SDK 实例
 */
function getArmsInstance() {
    if (typeof window === 'undefined') {
        return null;
    }
    return window.RumSDK?.default;
}

/**
 * 检查 ARMS SDK 是否已加载
 */
function isArmsReady(): boolean {
    return getArmsInstance() !== null && getArmsInstance() !== undefined;
}

/**
 * SDK 异步加载，调用的时候可能仍未加载完成
 * 等待 ARMS SDK 准备就绪后执行回调
 * @param callback 回调函数
 */
export function onArmsReady<T = void>(callback: () => T): void {
    if (isArmsReady()) {
        callback();
    } else if (typeof window !== 'undefined') {
        window.addEventListener('ARMSReady', callback, { once: true });
    }
}

/**
 * 上报自定义事件（内部实现）
 */
function _reportCustomEvent(
    eventName: string,
    eventType: string,
    options?: {
        group?: string;
        value?: number;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        properties?: Record<string, any>;
    }
): boolean {
    try {
        const instance = getArmsInstance();
        instance!.sendCustom({
            type: eventType,
            name: eventName,
            ...(options?.group && { group: options.group }),
            ...(options?.value !== undefined && { value: options.value }),
            ...(options?.properties && { properties: options.properties }),
        });
        return true;
    } catch (error) {
        console.error('[ARMS] Failed to report custom event:', error);
        return false;
    }
}

/**
 * 上报自定义事件
 * @param eventName 事件名称（必填）
 * @param eventType 事件类型（必填），用于区分不同类型的事件
 * @param options 可选参数
 * @param options.group 事件分组
 * @param options.value 事件数值（用于统计数值型数据）
 * @param options.properties 自定义属性
 * @returns 是否上报成功（SDK 未就绪时返回 false，会在就绪后异步上报）
 */
export function reportCustomEvent(
    eventName: string,
    eventType: string,
    options?: {
        group?: string;
        value?: number;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        properties?: Record<string, any>;
    }
): boolean {
    if (isArmsReady()) {
        return _reportCustomEvent(eventName, eventType, options);
    }

    onArmsReady(() => {
        _reportCustomEvent(eventName, eventType, options);
    });
    return false;
}

/**
 * 上报自定义异常（内部实现）
 */
function _reportException(
    exceptionName: string,
    message: string,
    options?: {
        file?: string;
        stack?: string;
        line?: number;
        column?: number;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        properties?: Record<string, any>;
    }
): boolean {
    try {
        const instance = getArmsInstance();
        instance!.sendException({
            name: exceptionName,
            message,
            ...(options?.file && { file: options.file }),
            ...(options?.stack && { stack: options.stack }),
            ...(options?.line !== undefined && { line: options.line }),
            ...(options?.column !== undefined && { column: options.column }),
            ...(options?.properties && { properties: options.properties }),
        });
        return true;
    } catch (error) {
        console.error('[ARMS] Failed to report exception:', error);
        return false;
    }
}

/**
 * 上报自定义异常
 * @param exceptionName 异常名称（必填）
 * @param message 异常信息（必填）
 * @param options 可选参数
 * @param options.file 异常发生文件
 * @param options.stack 异常堆栈信息
 * @param options.line 异常发生的行数
 * @param options.column 异常发生的列数
 * @param options.properties 自定义属性
 * @returns 是否上报成功（SDK 未就绪时返回 false，会在就绪后异步上报）
 */
export function reportException(
    exceptionName: string,
    message: string,
    options?: {
        file?: string;
        stack?: string;
        line?: number;
        column?: number;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        properties?: Record<string, any>;
    }
): boolean {
    if (isArmsReady()) {
        return _reportException(exceptionName, message, options);
    }

    onArmsReady(() => {
        _reportException(exceptionName, message, options);
    });
    return false;
}

/**
 * 上报自定义资源（内部实现）
 */
function _reportResource(
    resourceName: string,
    resourceType: string,
    duration: number,
    options?: {
        success?: number;
        method?: string;
        status_code?: number | string;
        message?: string;
        url?: string;
        trace_id?: string;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        properties?: Record<string, any>;
    }
): boolean {
    try {
        const instance = getArmsInstance();
        instance!.sendResource({
            name: resourceName,
            type: resourceType,
            duration,
            ...(options?.success !== undefined && { success: options.success }),
            ...(options?.method && { method: options.method }),
            ...(options?.status_code !== undefined && { status_code: options.status_code }),
            ...(options?.message && { message: options.message }),
            ...(options?.url && { url: options.url }),
            ...(options?.trace_id && { trace_id: options.trace_id }),
            ...(options?.properties && { properties: options.properties }),
        });
        return true;
    } catch (error) {
        console.error('[ARMS] Failed to report resource:', error);
        return false;
    }
}

/**
 * 上报自定义资源
 * @param resourceName 资源名称（必填）
 * @param resourceType 资源类型（必填），例如：css、javascript、xmlhttprequest、fetch、api、image、font、other
 * @param duration 请求耗时（必填，单位：毫秒）
 * @param options 可选参数
 * @param options.success 请求成功状态：1-成功，0-失败，-1-未知
 * @param options.method 请求方法
 * @param options.status_code 请求状态码
 * @param options.message 请求消息
 * @param options.url 请求地址
 * @param options.trace_id 链路追踪ID
 * @param options.properties 自定义属性
 * @returns 是否上报成功（SDK 未就绪时返回 false，会在就绪后异步上报）
 */
export function reportResource(
    resourceName: string,
    resourceType: string,
    duration: number,
    options?: {
        success?: number;
        method?: string;
        status_code?: number | string;
        message?: string;
        url?: string;
        trace_id?: string;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        properties?: Record<string, any>;
    }
): boolean {
    if (isArmsReady()) {
        return _reportResource(resourceName, resourceType, duration, options);
    }

    onArmsReady(() => {
        _reportResource(resourceName, resourceType, duration, options);
    });
    return false;
}

/**
 * 批量上报自定义事件（内部实现）
 */
function _reportBatchEvents(
    events: Array<{
        name: string;
        type: string;
        group?: string;
        value?: number;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        properties?: Record<string, any>;
    }>
): number {
    let successCount = 0;
    events.forEach(event => {
        if (
            _reportCustomEvent(event.name, event.type, {
                group: event.group,
                value: event.value,
                properties: event.properties,
            })
        ) {
            successCount += 1;
        }
    });
    return successCount;
}

/**
 * 批量上报自定义事件
 * @param events 事件数组，每个事件包含 name、type 和可选的 options
 * @returns 成功上报的事件数量（SDK 未就绪时返回 0，会在就绪后异步上报）
 */
export function reportBatchEvents(
    events: Array<{
        name: string;
        type: string;
        group?: string;
        value?: number;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        properties?: Record<string, any>;
    }>
): number {
    if (isArmsReady()) {
        return _reportBatchEvents(events);
    }

    onArmsReady(() => {
        _reportBatchEvents(events);
    });
    return 0;
}

/**
 * 设置 ARMS 配置（内部实现）
 */
function _setArmsConfig(config: Record<string, unknown>): boolean {
    if (!isArmsReady()) {
        return false;
    }

    const instance = getArmsInstance();
    if (!instance) {
        return false;
    }

    instance.setConfig(config);
    return true;
}

/**
 * 设置 ARMS 运行时配置
 * @returns 是否设置成功（SDK 未就绪时返回 false，会在就绪后异步设置）
 */
export function setArmsConfig(config: Record<string, unknown>): boolean {
    if (isArmsReady()) {
        return _setArmsConfig(config);
    }

    onArmsReady(() => {
        _setArmsConfig(config);
    });
    return false;
}
